import { useNavigate } from "@tanstack/react-router";
import { Users } from "lucide-react";
import { useState } from "react";
import { DetailSection } from "@/components/shared/detail-section";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { interactiveRowProps } from "@/components/shared/interactive-row";
import { LoadingRows } from "@/components/shared/loading-rows";
import { StatusPill } from "@/components/shared/status-badge";
import { EntityCombobox } from "@/components/ui/entity-combobox";
import { useTeamCapabilityGaps } from "@/hooks/use-capability-gaps";
import { carryListSearch } from "@/lib/list-search";

/**
 * Capability gaps across a group — EPIC-023, WI-308.
 *
 * Two readings of one set of numbers. The competency rollup answers "what is
 * this team short on", worst first, which is the question you cannot answer
 * by clicking through profiles. The people table answers "who", and links
 * out to the individual view.
 *
 * Like the profile tab, only RATED capabilities count. A competency nobody
 * on the team has been rated on simply isn't a row.
 */
export function TeamCapabilityGaps() {
  const navigate = useNavigate();
  // Null = the viewer's own direct reports, which is the manager's default
  // question. Picking a role is how HR looks across a whole discipline.
  const [roleId, setRoleId] = useState<string | null>(null);
  const { data, isLoading, isError, error, refetch } = useTeamCapabilityGaps(roleId);

  const picker = (
    <div className="max-w-sm">
      <EntityCombobox
        entity="roles"
        value={roleId}
        onChange={setRoleId}
        includeNone
        placeholder="My direct reports"
        searchPlaceholder="Search roles…"
      />
    </div>
  );

  if (isLoading) {
    return (
      <div className="space-y-4">
        {picker}
        <LoadingRows />
      </div>
    );
  }
  if (isError) {
    return (
      <div className="space-y-4">
        {picker}
        <ErrorState
          message={error?.message ?? "Couldn't load team capabilities"}
          onRetry={() => refetch()}
        />
      </div>
    );
  }
  if (!data || data.people.length === 0) {
    return (
      <div className="space-y-4">
        {picker}
        <EmptyState
          icon={Users}
          title="Nobody to show"
          description={
            roleId
              ? "Nobody active holds this role as their primary one."
              : "You have no direct reports. Pick a role above to look across a discipline instead."
          }
        />
      </div>
    );
  }

  const anyRated = data.people.some((p) => p.rated > 0);

  return (
    <div className="space-y-4">
      {picker}

      {/* The scope is named by the picker directly above, so the title does
          not repeat it — and lowercasing a role name to fit a sentence read
          as "what ai engineer are short on". */}
      <DetailSection title="Where the gaps are" boldTitle className="gap-3 py-4">
        {!anyRated ? (
          <p className="text-muted-foreground text-sm">
            {data.scopeLabel}: nobody has been rated on a required capability in a closed review
            round yet, so there is no pattern to show.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground text-xs">
                  <th className="px-3 py-2 font-medium">Competency</th>
                  <th className="px-3 py-2 font-medium text-right">Below</th>
                  <th className="px-3 py-2 font-medium text-right">Rated</th>
                  <th className="px-3 py-2 font-medium text-right">Team average</th>
                </tr>
              </thead>
              <tbody>
                {data.competencies.map((row) => (
                  <tr key={row.competencyId} className="border-b last:border-b-0">
                    <td className="px-3 py-2.5 font-medium text-foreground">
                      {row.competencyName}
                      {row.categoryName && (
                        <span className="block text-2xs text-muted-foreground">
                          {row.categoryName}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      {row.belowPeople > 0 ? (
                        <StatusPill
                          tone="warning"
                          label={`${row.belowPeople} of ${row.ratedPeople}`}
                        />
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right text-muted-foreground tabular-nums">
                      {row.ratedPeople}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono tabular-nums">
                      {row.averageOrdinal.toFixed(1)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DetailSection>

      <DetailSection title="Person by person" boldTitle className="gap-3 py-4">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-muted-foreground text-xs">
                <th className="px-3 py-2 font-medium">Person</th>
                <th className="px-3 py-2 font-medium">Role</th>
                <th className="px-3 py-2 font-medium">Standing</th>
                <th className="px-3 py-2 font-medium text-right">Rated</th>
              </tr>
            </thead>
            <tbody>
              {data.people.map((p) => (
                <tr
                  key={p.personId}
                  {...interactiveRowProps(
                    () =>
                      navigate({
                        to: "/dashboard/persons/$personId",
                        params: { personId: p.personId },
                        search: { ...carryListSearch(), tab: "capabilities" },
                      }),
                    `Open ${p.personName}`,
                  )}
                >
                  <td className="px-3 py-2.5 font-medium text-foreground">{p.personName}</td>
                  <td className="px-3 py-2.5 text-muted-foreground">
                    {p.roleName ?? "—"}
                    {p.roleLevelName && (
                      <span className="text-muted-foreground"> — {p.roleLevelName}</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    {p.rated === 0 ? (
                      <span className="text-muted-foreground text-xs">Nothing rated</span>
                    ) : (
                      <span className="flex flex-wrap items-center gap-1.5">
                        {p.meets > 0 && <StatusPill tone="success" label={`${p.meets} meets`} />}
                        {p.below > 0 && <StatusPill tone="warning" label={`${p.below} below`} />}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-right text-muted-foreground tabular-nums">
                    {p.rated} / {p.required}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DetailSection>
    </div>
  );
}
